// src/pages/storePages/manageStore/CategoryFormPage.js
import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  storeGetCategoryBySlug,
  storeGetAllCategories,
  storeCreateCategory,
  storeUpdateCategory,
} from "../../../storeAPIs/StoreCategoriesApis";
import "../../../static/storecss/manageStore/shared.css";
import "../../../static/storecss/manageStore/CategoryFormPage.css";

export default function CategoryFormPage() {
  const { slug } = useParams(); // present when editing
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const isEdit = Boolean(slug);
  const initialParentId = searchParams.get("parent") || "";
  const returnTo = searchParams.get("returnTo") || "/manage-store";

  const [name, setName] = useState("");
  const [slugValue, setSlugValue] = useState("");
  const [parentId, setParentId] = useState(initialParentId);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const navigatedRef = useRef(false);

  // Load all categories for the parent dropdown + the category being edited
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const tasks = [storeGetAllCategories()];
    if (isEdit) tasks.push(storeGetCategoryBySlug(slug));

    Promise.all(tasks)
      .then(([catsRes, catRes]) => {
        if (cancelled) return;
        setCategories(catsRes.data?.results ?? catsRes.data ?? []);
        if (catRes) {
          const c = catRes.data;
          setName(c.name);
          setSlugValue(c.slug);
          setParentId(c.parent ? String(c.parent) : "");
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Failed to load.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug, isEdit]);

  // Auto-fill slug from name
  useEffect(() => {
    if (!isEdit) {
      setSlugValue(
        name
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "")
      );
    }
  }, [name, isEdit]);

  const handleCancel = () => {
    if (navigatedRef.current) return;
    navigatedRef.current = true;
    navigate(returnTo, { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (navigatedRef.current) return;
    setSubmitting(true);
    setError(null);

    const payload = {
      name: name.trim(),
      slug: slugValue.trim(),
      parent: parentId ? Number(parentId) : null,
    };

    try {
      if (isEdit) {
        await storeUpdateCategory(slug, payload);
      } else {
        await storeCreateCategory(payload);
      }
      navigatedRef.current = true;
      navigate(returnTo, { replace: true });
    } catch (err) {
      const data = err.response?.data;
      if (data && typeof data === "object") {
        setError(
          Object.entries(data)
            .map(([k, v]) => `${k}: ${v}`)
            .join(" · ")
        );
      } else {
        setError(err.message || "Failed to save.");
      }
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="manage-store">
        <p className="manage-loading">Loading…</p>
      </div>
    );
  }

  return (
    <div className="manage-store">
      <header className="manage-header">
        <h1>{isEdit ? "Edit Category" : "New Category"}</h1>
      </header>

      {error && <p className="manage-error">{error}</p>}

      <form className="manage-form" onSubmit={handleSubmit}>
        <div className="form-row">
          <label>Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="e.g. Temperature Sensors"
          />
        </div>

        <div className="form-row">
          <label>Slug</label>
          <input
            type="text"
            value={slugValue}
            onChange={(e) => setSlugValue(e.target.value)}
            required
            placeholder="temperature-sensors"
          />
        </div>

        <div className="form-row">
          <label>Parent Category</label>
          <select
            value={parentId}
            onChange={(e) => setParentId(e.target.value)}
          >
            <option value="">— None (root category) —</option>
            {categories
              .filter((c) => !isEdit || c.slug !== slug)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </select>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? "Saving…" : isEdit ? "Save Changes" : "Create Category"}
          </button>
        </div>
      </form>
    </div>
  );
}